<template>
    <DModal
        is-big
        :title="__('administrations.queue_jobs.modal.title')"
        :show="showModal"
        @close="closeModal"
    >
        <DCard>
            <DCardBody>
                <DRow class="detail-row">
                    <DCol>
                        <span class="fw-bold">{{ __('administrations.queue_jobs.table.updated_at') }}:</span>
                        <span v-if="queueJob.updated_at">{{ queueJob.updated_at }}</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('administrations.queue_jobs.table.queue') }}:</span>
                        <span v-if="queueJob.queue">{{ queueJob.queue }}</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('administrations.queue_jobs.table.action') }}:</span>
                        <span v-if="queueJob.action">{{ queueJob.action }}</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('administrations.queue_jobs.table.filter') }}:</span>
                        <span v-if="queueJob.filter">{{ queueJob.filter }}</span>
                    </DCol>
                </DRow>
                <DRow class="detail-row">
                    <DCol>
                        <span class="fw-bold">{{ __('administrations.queue_jobs.table.status') }}:</span>
                        <span v-if="queueJob.status">{{ queueJob.status }}</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('administrations.queue_jobs.table.reference') }}:</span>
                        <span v-if="queueJob.reference">{{ queueJob.reference }}</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('administrations.queue_jobs.table.last_error') }}:</span>
                        <span v-if="queueJob.last_error">
                            <div
                                v-for="error in nl2array(queueJob.last_error)"
                                :key="error"
                            >
                                {{ error }}
                            </div>
                        </span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('administrations.queue_jobs.table.available_at') }}:</span>
                        <span v-if="queueJob.available">{{ queueJob.available }}</span>
                    </DCol>
                </DRow>
            </DCardBody>
        </DCard>
        <DCard>
            <DCardBody>
                <div
                    v-if="loading"
                    class="text-center py-3"
                >
                    {{ __('messages.loading') }}
                </div>
                <div
                    v-else-if="loadFailed"
                    class="text-danger py-3"
                >
                    {{ __('administrations.queue_jobs.modal.tries_failed') }}
                </div>
                <div
                    v-else-if="!attempts.length"
                    class="py-3"
                >
                    {{ __('administrations.queue_jobs.modal.no_tries') }}
                </div>
                <template v-else>
                    <!--
                        Says outright when the list is capped, rather than truncating silently on
                        the rows worth investigating -- production holds requests with over fifty
                        thousand attempts. Only live mode caps, at 100; the archive returns every
                        stored attempt, so total equals shown and this never renders there.
                    -->
                    <div
                        v-if="total > shown"
                        class="text-muted pb-2"
                    >
                        {{ __('administrations.queue_jobs.modal.tries_truncated', { shown: shown, total: total }) }}
                    </div>
                    <DTable
                        :items="attempts"
                        :fields="fields"
                    >
                        <template #updated_at="{item}">
                            <td>{{ item.updated_at ? toDateTimeString(item.updated_at) : '' }}</td>
                        </template>
                        <template #status="{item}">
                            <td>
                                {{ item.successful ? __('administrations.queue_jobs.status.successful') : __('administrations.queue_jobs.status.failed') }}
                            </td>
                        </template>
                        <template #error="{item}">
                            <td>
                                <div
                                    v-for="error in nl2array(item.error)"
                                    :key="error"
                                >
                                    {{ error }}
                                </div>
                            </td>
                        </template>
                    </DTable>
                </template>
            </DCardBody>
        </DCard>
    </DModal>
</template>
<script>
import nl2array from '~@/Mixins/nl2array'

export default {
    name: 'ShowQueuesJobModal',
    mixins: [nl2array],
    props: {
        queueJob: {
            type: Object,
            default: () => { return {} }
        },
        showModal: {
            type: Boolean,
            default: false
        },
        /** Fetch attempts from the archive endpoint; the live one is bound to a deleted row. */
        archived: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close-modal'],
    data () {
        return {
            fields: [
                { key: 'updated_at', label: this.__('tools.fulfillment.date') },
                { key: 'status', label: this.__('tools.fulfillment.status') },
                { key: 'error', label: this.__('tools.fulfillment.error') }
            ],
            attempts: [],
            total: 0,
            shown: 0,
            loading: false,
            loadFailed: false
        }
    },
    mounted () {
        this.loadTries()
    },
    methods: {
        /**
         * The two modes differ only in which endpoint answers. The live route is model-bound to a
         * row archiving deletes, so the archive one takes the request id as a query parameter.
         * Both return the same {tries, total, shown} shape.
         */
        triesUrl () {
            if (this.archived) {
                return this.$route('administrations.queue.archive-tries', { requestId: this.queueJob.id })
            }

            return this.$route('administrations.queue.tries', { jobRequest: this.queueJob.id })
        },
        /**
         * Fetched here rather than arriving with the list row: the list carries only the newest
         * attempt, and production holds requests with over fifty thousand. The server returns
         * them newest first with the true total, so nothing sorts client-side.
         */
        async loadTries () {
            if (!this.queueJob?.id) {
                return
            }

            this.loading = true
            this.loadFailed = false

            try {
                const response = await fetch(this.triesUrl(), { headers: { Accept: 'application/json' } })

                if (!response.ok) {
                    throw new Error(`Unexpected response ${response.status}`)
                }

                const payload = await response.json()

                // A degraded read answers 200 with an empty list, so this flag is all that
                // separates "unreadable" from "this request had none". The live endpoint omits
                // the key, and undefined is not true, leaving live behaviour unchanged.
                if (payload.readFailed === true) {
                    this.loadFailed = true

                    return
                }

                this.attempts = payload.tries ?? []
                this.total = payload.total ?? 0
                this.shown = payload.shown ?? this.attempts.length
            } catch {
                this.loadFailed = true
            } finally {
                this.loading = false
            }
        },
        toDateTimeString (value) {
            const date = new Date(value)
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
        },
        closeModal () {
            this.$emit('close-modal')
        }
    }
}
</script>

<style scoped>
.detail-row {
    padding: 20px 0;
}
</style>
